import { Injectable, Injector, isDevMode } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
} from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { DeviceTokenStorageService } from '@marina/utils';
import { SessionTimeoutService } from '../services/session-timeout.service';
import { TokenRefreshCoordinator, StaleSessionError } from '../services/token-refresh-coordinator.service';
import { AuthSessionTerminatorService } from '../services/auth-session-terminator.service';
import { UserActivityService } from '../services/user-activity.service';
import { isAuthEndpoint, isPublicResourceEndpoint, isRefreshEndpoint } from '../utils/auth-endpoints.util';
import { httpStatusOf, isSessionExpiredError, isTerminalAuthFailure } from '../utils/http-status.util';

/**
 * Treat the access token as spent this long before it actually expires.
 *
 * The server allows no clock skew and the cookie expires at exactly the token's `exp`, so a client
 * clock running a few seconds behind produces a window where we believe the token is good and every
 * request 401s. With the recovery below that costs a refresh and a retry rather than a logout, but
 * there is no reason to spend them.
 */
const EXPIRY_MARGIN_MS = 30_000;

/**
 * Authentication for outgoing requests, and recovery for 401s.
 *
 * Tokens live in httpOnly cookies, so there is no Authorization header to add; what this does is
 * attach the marina/customer/device headers, keep `withCredentials` on, and decide what a 401 means.
 *
 * **Ordering matters and is not obvious.** Angular builds the interceptor chain with `reduceRight`
 * over the provided order, so the *first* provided interceptor is outermost — it sees requests first
 * and errors **last**. This interceptor must therefore be registered **after** `ErrorInterceptor` so
 * that it sits inside it and receives the raw `HttpErrorResponse`. With the order the other way
 * round, `ErrorInterceptor` replaced every error with an `ApiError` before this code saw it, and
 * since `ApiError` exposed `statusCode` rather than `status`, the `error.status === 401` test here
 * never matched: the whole recovery path below was unreachable, and a recoverable 401 became a
 * logout. `httpStatusOf` is used throughout so that a repeat of that mistake degrades into a working
 * retry instead of silence.
 *
 * Uses Injector for lazy resolution of AuthService and SessionTimeoutService to break the circular
 * dependency HttpClient → AuthInterceptor → AuthService → HttpClient.
 */
@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private _authService?: AuthService;
  private _sessionTimeoutService?: SessionTimeoutService | null;
  private _userActivity?: UserActivityService | null;

  constructor(
    private injector: Injector,
    private deviceTokenStorageService: DeviceTokenStorageService,
    private coordinator: TokenRefreshCoordinator,
    private terminator: AuthSessionTerminatorService,
  ) {}

  private get authService(): AuthService {
    return (this._authService ??= this.injector.get(AuthService));
  }

  /** Lazy, like the others: this service's heartbeat goes through HttpClient, which needs this chain. */
  private get userActivity(): UserActivityService | null {
    if (this._userActivity === undefined) {
      try {
        this._userActivity = this.injector.get(UserActivityService);
      } catch {
        this._userActivity = null;
      }
    }
    return this._userActivity;
  }

  private get sessionTimeoutService(): SessionTimeoutService | null {
    if (this._sessionTimeoutService === undefined) {
      try {
        this._sessionTimeoutService = this.injector.get(SessionTimeoutService);
      } catch {
        this._sessionTimeoutService = null;
      }
    }
    return this._sessionTimeoutService;
  }

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    if (isPublicResourceEndpoint(request.url)) {
      return next.handle(request.clone({ withCredentials: true }));
    }

    // Login, refresh, logout, activate and friends carry their own credentials and must not be
    // wrapped in recovery — refreshing in order to retry a refresh is a loop.
    // `/auth/init` is deliberately NOT in that set: it is an ordinary protected endpoint, and a page
    // reload with an expired access token depends on it being refreshed and retried like any other.
    if (isAuthEndpoint(request.url)) {
      // Refresh is the one auth call that must *not* go out after the session ended: it is what
      // painted the login page with POST /auth/refresh 401. Logout still has to be sendable.
      if (this.terminator.isSessionOver() && isRefreshEndpoint(request.url)) {
        this.log('[AuthInterceptor] Session is over; not refreshing');
        return throwError(() => new StaleSessionError());
      }
      // BaseApiService stamps X-Customer-Id / X-Marina-Id onto every call, including refresh.
      // After the access cookie expires those headers make CustomerAccessMiddleware 401 the
      // rotation ("Authentication required to use X-Customer-Id") before the refresh action runs.
      return next.handle(this.withoutTenantScope(request));
    }

    // The session is over and we are on our way to the login page. Sending this would achieve
    // nothing: there are no credentials, so it 401s, which triggers a refresh against cookies the
    // server has already cleared, which 401s in turn — three failures in the console for a request
    // whose answer nobody is waiting for. Fail it here instead, quietly and without a round trip.
    if (this.terminator.isSessionOver()) {
      this.log('[AuthInterceptor] Session is over; not sending', request.url);
      return throwError(() => new StaleSessionError());
    }

    // Capture who we are *now*. Everything that happens later is judged against this: a response for
    // a login the user has since replaced must not touch the new one, and a 401 that was already
    // fixed by another tab's refresh needs a retry rather than a second rotation.
    const sessionId = this.coordinator.currentSessionId();
    const revision = this.coordinator.currentRevision();

    // Local expiry timestamps schedule work; they never decide that a session is over. They come from
    // localStorage and are compared against the client clock, either of which can be wrong — another
    // tab may have refreshed without this one noticing, or the clock may simply be fast. When they
    // say the token is spent, we ask the server; the server's answer is what counts.
    if (this.accessTokenLooksSpent()) {
      this.log('[AuthInterceptor] Access token spent by local reckoning; refreshing before the request');
      return this.refreshThenSend(request, next, sessionId, revision);
    }

    return next.handle(this.buildAuthenticatedRequest(request)).pipe(
      tap((event) => {
        if (event.type === 4 && this.sessionTimeoutService) {
          this.sessionTimeoutService.reset();
        }
      }),
      catchError((error: unknown) => {
        if (httpStatusOf(error) !== 401) {
          return throwError(() => error);
        }

        // The server said the session itself is over. No refresh can fix that.
        if (isSessionExpiredError(error)) {
          this.log('[AuthInterceptor] Server reports the session has ended');
          this.terminator.end('session-expired', sessionId, { sessionExpired: true });
          return throwError(() => error);
        }

        return this.refreshThenSend(request, next, sessionId, revision);
      })
    );
  }

  /**
   * Make the credentials current, then send the request once.
   *
   * The two failures are kept in separate scopes on purpose. They used to share one `catchError`
   * placed after the `switchMap`, which meant it caught errors from the refresh *and* from the
   * retried request — so a successful refresh followed by an ordinary 403, 422, 429 or 500 from the
   * endpoint was recorded as a refresh failure and logged the user out. An API error is the caller's
   * business; only an auth failure is ours.
   */
  private refreshThenSend(
    request: HttpRequest<unknown>,
    next: HttpHandler,
    sessionId: string | null,
    revision: number,
  ): Observable<HttpEvent<unknown>> {
    // Somebody already refreshed this login: the cookies in the jar are newer than the ones this
    // request went out with, so it just needs sending again.
    const recovered$ = this.coordinator.revisionAdvancedSince(sessionId, revision)
      ? of(undefined)
      : this.coordinator.getOrStart('interceptor', sessionId);

    return recovered$.pipe(
      // --- refresh scope -------------------------------------------------------------------
      catchError((refreshError: unknown) => {
        if (refreshError instanceof StaleSessionError) {
          return throwError(() => refreshError);
        }

        if (isTerminalAuthFailure(refreshError)) {
          // The credential was rejected outright — this is the end of the session.
          this.terminator.end('refresh-rejected', sessionId, {
            sessionExpired: isSessionExpiredError(refreshError),
          });
        } else {
          // Network failure, 429, 5xx, or a cooldown from an earlier attempt. Temporary by
          // definition: surface it and keep the session. Treating these as terminal is what turned
          // a dropped connection into a forced logout.
          this.log('[AuthInterceptor] Refresh failed temporarily; session kept');
        }

        return throwError(() => refreshError);
      }),

      switchMap(() => {
        // The login may have been replaced while we waited for the refresh. Sending now would run
        // this request under somebody else's credentials.
        if (sessionId && !this.terminator.isCurrent(sessionId)) {
          return throwError(() => new StaleSessionError());
        }

        // --- retry scope -------------------------------------------------------------------
        return next.handle(this.buildAuthenticatedRequest(request)).pipe(
          tap((event) => {
            if (event.type === 4 && this.sessionTimeoutService) {
              this.sessionTimeoutService.reset();
            }
          }),
          catchError((retryError: unknown) => {
            if (httpStatusOf(retryError) === 401) {
              // Fresh credentials and still refused: refreshing again would loop.
              this.terminator.end('retry-401', sessionId, {
                sessionExpired: isSessionExpiredError(retryError),
              });
            }
            // Anything else — 403, 404, 409, 422, 429, 5xx — belongs to whoever made the call.
            return throwError(() => retryError);
          })
        );
      })
    );
  }

  /**
   * True when the access token is at or past its expiry, allowing a margin for clock drift.
   * Absent timestamps mean "we do not know", which is not the same as "expired".
   */
  private accessTokenLooksSpent(): boolean {
    const expiresAt = this.authService.getAccessTokenExpiresAt();
    if (!expiresAt) {
      return false;
    }
    return expiresAt.getTime() - EXPIRY_MARGIN_MS <= Date.now();
  }

  /**
   * Build request with auth headers and credentials.
   * Web apps use httpOnly cookies for tokens — no Authorization header needed.
   *
   * §2.4 / §1.1 — Header attachment rules:
   *   X-Marina-Id  — authenticated + NOT a customer/system surface path.
   *   X-Customer-Id — authenticated + a customer surface path OR customer ID is known
   *                   (ensures it is always forwarded even on legacy /api/customers/ paths).
   */
  private buildAuthenticatedRequest(request: HttpRequest<unknown>): HttpRequest<unknown> {
    let headers = request.headers;

    if (this.authService.isAuthenticated()) {
      if (!this.isCustomerOrSystemPath(request.url)) {
        const marinaId = this.authService.getCurrentMarinaId();
        if (marinaId && !headers.has('X-Marina-Id')) {
          headers = headers.set('X-Marina-Id', marinaId);
        }
      }

      // X-Customer-Id — attach whenever the authenticated user has a customer ID,
      // and the header has not already been set by BaseApiService.
      const customerId = this.authService.getCustomerId();
      if (customerId && !headers.has('X-Customer-Id')) {
        headers = headers.set('X-Customer-Id', customerId);
      }
    }

    const deviceToken = this.deviceTokenStorageService.getDeviceTokenSync();
    if (deviceToken && !headers.has('X-Device-Token')) {
      headers = headers.set('X-Device-Token', deviceToken);
    }

    // Tells the server this request was made while somebody was actually using the page, as opposed
    // to a poll or a timer. Only requests carrying it extend the idle window — which is what stops
    // an untouched dashboard from keeping itself signed in indefinitely.
    if (this.userActivity?.hasRecentInput() && !headers.has('X-User-Activity')) {
      headers = headers.set('X-User-Activity', '1');
    }

    return request.clone({
      headers,
      withCredentials: true,
    });
  }

  /**
   * Auth cookie-flow calls must not carry tenant headers. Refresh in particular is sent after
   * the access cookie is gone; a leftover X-Customer-Id is refused before rotation.
   */
  private withoutTenantScope(request: HttpRequest<unknown>): HttpRequest<unknown> {
    let headers = request.headers;
    if (headers.has('X-Customer-Id')) {
      headers = headers.delete('X-Customer-Id');
    }
    if (headers.has('X-Marina-Id')) {
      headers = headers.delete('X-Marina-Id');
    }
    return request.clone({
      headers,
      withCredentials: true,
    });
  }

  /**
   * Returns true for customer and system API surfaces that must not receive X-Marina-Id.
   * Portal paths (/api/v1/portal/) and legacy marina-scoped paths continue to receive it.
   */
  private isCustomerOrSystemPath(url: string): boolean {
    return url.includes('/api/v1/customer/') || url.includes('/api/v1/system/');
  }

  private log(...args: unknown[]): void {
    if (isDevMode()) {
      console.log(...args);
    }
  }
}
